import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';
import Icon from '../../components/base/Icon';

const REVIEW_FORM_URL = 'https://readdy.ai/api/form/d6pfkblsbgrc47filmp0';

const SERVICE_AREAS = [
  'Boise', 'Meridian', 'Nampa', 'Caldwell', 'Eagle',
  'Star', 'Kuna', 'Middleton', 'Garden City', 'Beacon Light',
];

const SERVICE_OPTIONS = [
  { value: '', label: 'Select a service...' },
  { value: 'Fence Installation & Repair', label: 'Fence Installation & Repair' },
  { value: 'Handyman Services', label: 'Handyman Services' },
  { value: 'Bathroom Remodel', label: 'Bathroom Remodel' },
  { value: 'Small Remodel Project', label: 'Small Remodel Project' },
  { value: 'Other', label: 'Other' },
];

const REVIEWS = [
  {
    name: 'Maralee Ainsworth',
    city: 'Caldwell, ID',
    rating: 5,
    service: 'Handyman Services',
    date: 'March 2025',
    text: 'Kole is skilled, reliable, and takes great pride in his work. He consistently arrived on time, communicated clearly, and followed through exactly as promised. His workmanship is excellent and the results have always been durable, clean, and well-finished.',
  },
  {
    name: 'Julie Masner',
    city: 'Beacon Light, ID',
    rating: 5,
    service: 'Fence Installation',
    date: 'February 2025',
    text: 'I highly recommend Kole Southern of Fix Right Services! He\'s professional, skilled and keeps you informed of the project needs and progress. He\'s prompt and very honest and reliable. I was very happy with the results of his work.',
  },
  {
    name: 'Maralee Ainsworth',
    city: 'Passero Ridge, ID',
    rating: 5,
    service: 'Handyman Services',
    date: 'January 2025',
    text: 'I am pleased to write this letter of recommendation for Kole Southern of Fix Right Services, who has provided outstanding handyman services for me on multiple occasions. He is reliable, punctual, and always follows through on his commitments.',
  },
];

interface ReviewFormState {
  name: string;
  city: string;
  service: string;
  rating: string;
  review: string;
}

const emptyForm = (): ReviewFormState => ({
  name: '',
  city: '',
  service: '',
  rating: '5',
  review: '',
});

function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1" role="group" aria-label="Star rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="w-10 h-10 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
        >
          <Icon
            name={(hovered || value) >= star ? 'star' : 'star-regular'}
            className={`w-7 h-7 ${(hovered || value) >= star ? 'text-[#B45309]' : 'text-gray-300'}`}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewForm() {
  const [values, setValues] = useState<ReviewFormState>(emptyForm());
  const [rating, setRating] = useState(5);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = (): string => {
    if (!values.name.trim()) return 'Your name is required.';
    if (!values.city.trim()) return 'City is required.';
    if (!values.service) return 'Please select the service you used.';
    if (!values.review.trim()) return 'Please write your review.';
    if (values.review.length > 500) return 'Review must be 500 characters or fewer.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const err = validate();
    if (err) { setErrorMsg(err); setStatus('error'); return; }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const params = new URLSearchParams();
      params.append('name', values.name);
      params.append('city', values.city);
      params.append('service', values.service);
      params.append('rating', String(rating));
      params.append('review', values.review);
      const res = await fetch(REVIEW_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (!res.ok) throw new Error('Failed');
      setStatus('success');
      setValues(emptyForm());
      setRating(5);
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or call us at (208) 204-6331.');
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm bg-white';

  if (status === 'success') {
    return (
      <div className="text-center py-12 px-6">
        <span className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
          <Icon name="check" className="w-8 h-8 text-green-600" />
        </span>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600 text-sm">
          We really appreciate you taking the time to share your experience. Your feedback helps other Treasure Valley homeowners find trusted help.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-readdy-form id="review-submission-form" noValidate>
      {status === 'error' && errorMsg && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Icon name="circle-exclamation" className="w-4 h-4" />
          </span>
          <span>{errorMsg}</span>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="review-name" className="block text-sm font-medium text-gray-700 mb-1">
            Your Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="review-name"
            name="name"
            required
            value={values.name}
            onChange={handleChange}
            className={inputClass}
            placeholder="Jane Smith"
          />
        </div>
        <div>
          <label htmlFor="review-city" className="block text-sm font-medium text-gray-700 mb-1">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="review-city"
            name="city"
            required
            value={values.city}
            onChange={handleChange}
            className={inputClass}
            placeholder="e.g. Boise, Meridian, Nampa..."
          />
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="review-service" className="block text-sm font-medium text-gray-700 mb-1">
          Service Used <span className="text-red-500">*</span>
        </label>
        <select
          id="review-service"
          name="service"
          required
          value={values.service}
          onChange={handleChange}
          className={`${inputClass} cursor-pointer`}
        >
          {SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Rating <span className="text-red-500">*</span>
        </label>
        <StarRating value={rating} onChange={setRating} />
        <input type="hidden" name="rating" value={rating} />
      </div>

      <div className="mb-6">
        <label htmlFor="review-text" className="block text-sm font-medium text-gray-700 mb-1">
          Your Review <span className="text-red-500">*</span>
        </label>
        <textarea
          id="review-text"
          name="review"
          required
          rows={5}
          maxLength={500}
          value={values.review}
          onChange={handleChange}
          className={`${inputClass} resize-none`}
          placeholder="Tell us about your experience — what was the project, how did it go, would you recommend us?"
        />
        <p className="text-xs text-gray-500 mt-1 text-right">{values.review.length}/500 characters</p>
      </div>

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full bg-[#B45309] hover:bg-[#92400E] disabled:opacity-60 text-white px-6 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 text-base"
      >
        {status === 'submitting' ? (
          <>
            <span className="w-5 h-5 flex items-center justify-center animate-spin">
              <Icon name="circle-notch" className="w-4 h-4" />
            </span>
            Submitting...
          </>
        ) : (
          <>
            <span className="w-5 h-5 flex items-center justify-center">
              <Icon name="paper-plane" className="w-4 h-4" />
            </span>
            Submit My Review
          </>
        )}
      </button>
      <p className="text-xs text-gray-500 text-center mt-3">
        Reviews are read by our team and may be shared on this page.
      </p>
    </form>
  );
}

function ReviewCard({ review }: { review: typeof REVIEWS[0] }) {
  return (
    <article className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#B45309]/10 flex items-center justify-center flex-shrink-0">
            <span className="text-[#B45309] font-bold text-base">{review.name.charAt(0)}</span>
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">{review.name}</p>
            <p className="text-xs text-gray-500">{review.city}</p>
          </div>
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap">{review.date}</span>
      </div>
      <div className="flex gap-0.5 mb-1">
        {[...Array(review.rating)].map((_, i) => (
          <Icon key={i} name="star" className="w-4 h-4 text-[#B45309]" />
        ))}
      </div>
      <p className="text-xs text-[#B45309] font-medium mb-3">{review.service}</p>
      <p className="text-gray-600 text-sm leading-relaxed">&quot;{review.text}&quot;</p>
    </article>
  );
}

export default function ReviewsPage() {
  useSEO({
    title: 'Customer Reviews — Fix Right Services | Boise Fence & Handyman',
    description: 'Read real reviews from Fix Right Services customers in Boise, Meridian, Nampa & Treasure Valley. Licensed fence installation, handyman & remodel contractor. Leave your own review.',
    keywords: 'Fix Right Services reviews, Boise fence contractor reviews, handyman reviews Treasure Valley, Fix Right Services testimonials, Boise home remodel reviews',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/reviews`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      'name': 'Fix Right Services',
      'url': import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com',
      'telephone': '+12082046331',
      'email': 'Southernkole@gmail.com',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Boise',
        'addressRegion': 'ID',
        'addressCountry': 'US',
      },
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': '5',
        'reviewCount': '3',
        'bestRating': '5',
        'worstRating': '1',
      },
      'review': REVIEWS.map((r) => ({
        '@type': 'Review',
        'author': { '@type': 'Person', 'name': r.name },
        'reviewRating': { '@type': 'Rating', 'ratingValue': String(r.rating), 'bestRating': '5' },
        'reviewBody': r.text,
      })),
    },
  });

  return (
    <div className="min-h-screen bg-white">

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-8">
              <Link to="/">
                <img
                  src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp"
                  alt="Fix Right Services Logo"
                  width="180"
                  height="56"
                  className="h-14 w-auto"
                  loading="eager"
                />
              </Link>
              <div className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-700">
                <Link to="/" className="hover:text-[#B45309] transition-colors cursor-pointer">Home</Link>
                <Link to="/services" className="hover:text-[#B45309] transition-colors cursor-pointer">Services</Link>
                <Link to="/about" className="hover:text-[#B45309] transition-colors cursor-pointer">About</Link>
                <Link to="/reviews" className="text-[#B45309] font-semibold cursor-pointer">Reviews</Link>
                <Link to="/blog" className="hover:text-[#B45309] transition-colors cursor-pointer">Blog</Link>
                <Link to="/contact" className="hover:text-[#B45309] transition-colors cursor-pointer">Contact</Link>
              </div>
            </div>
            <a
              href="tel:+12082046331"
              className="flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              aria-label="Call Fix Right Services"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              <span className="hidden sm:inline">(208) 204-6331</span>
              <span className="sm:hidden">Call Now</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" aria-label="Reviews hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a369d93ac9067447f5eb739864aa489c.jpg"
            alt="Happy Fix Right Services customers in Boise"
            width="1920"
            height="650"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/65"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto w-full">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-white/70">
              <li><Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link></li>
              <li><span className="w-4 h-4 inline-flex items-center justify-center"><Icon name="chevron-right" className="w-3 h-3" /></span></li>
              <li className="text-white font-medium">Reviews</li>
            </ol>
          </nav>
          <div className="inline-flex items-center gap-2 bg-[#B45309]/90 text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><Icon name="star" className="w-3.5 h-3.5" /></span>
            5.0 — All 5-Star Reviews
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            What Our Customers Say
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-3xl">
            Real feedback from real Treasure Valley homeowners. We let our work — and our customers — speak for themselves.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="tel:+12082046331"
              className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-8 py-3.5 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              Call (208) 204-6331
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-900 px-8 py-3.5 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="file-lines" className="w-4 h-4" /></span>
              Request a Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Review Policy Banner */}
      <section className="bg-amber-50 border-y border-amber-200 py-8 px-4 sm:px-6 lg:px-8" aria-label="Review policy">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <span className="w-12 h-12 flex items-center justify-center bg-[#B45309]/10 rounded-xl flex-shrink-0">
              <Icon name="shield-halved" className="w-6 h-6 text-[#B45309]" />
            </span>
            <div>
              <h2 className="font-bold text-gray-900 mb-1">Our Review Policy</h2>
              <p className="text-gray-700 text-sm leading-relaxed">
                We collect reviews directly from customers we&apos;ve worked with. We don&apos;t pay for reviews, fabricate testimonials, or filter out negative feedback. Every review submitted through this page is read by our team. We believe honest feedback — good or constructive — makes us better.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Rating Summary */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="Rating summary">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="md:col-span-1 bg-white rounded-2xl border border-gray-200 p-8 flex flex-col items-center justify-center text-center">
              <p className="text-6xl font-bold text-[#B45309] mb-2">5.0</p>
              <div className="flex gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} name="star" className="w-5 h-5 text-[#B45309]" />
                ))}
              </div>
              <p className="text-gray-500 text-sm">Based on 3 reviews</p>
              <div className="mt-4 pt-4 border-t border-gray-100 w-full space-y-2">
                {[5, 4, 3, 2, 1].map((star) => (
                  <div key={star} className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="w-3 text-right">{star}</span>
                    <Icon name="star" className="w-3 h-3 text-[#B45309]" />
                    <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-[#B45309] h-full rounded-full"
                        style={{ width: star === 5 ? '100%' : '0%' }}
                      />
                    </div>
                    <span className="w-4 text-right">{star === 5 ? '3' : '0'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="md:col-span-3 grid sm:grid-cols-3 gap-4">
              {[
                { icon: 'clock', label: 'On Time', value: '100%', sub: 'of customers say we arrived on time' },
                { icon: 'comments', label: 'Communication', value: '5/5', sub: 'rated our communication excellent' },
                { icon: 'star', label: 'Quality', value: '5/5', sub: 'rated our workmanship excellent' },
              ].map((stat) => (
                <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col items-center text-center">
                  <span className="w-12 h-12 flex items-center justify-center bg-[#B45309]/10 rounded-xl mb-3">
                    <Icon name={stat.icon} className="w-6 h-6 text-[#B45309]" />
                  </span>
                  <p className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</p>
                  <p className="font-semibold text-gray-800 text-sm mb-1">{stat.label}</p>
                  <p className="text-gray-500 text-xs">{stat.sub}</p>
                </div>
              ))}
              <div className="sm:col-span-3 bg-white rounded-xl border border-gray-200 p-6">
                <p className="text-sm text-gray-600 leading-relaxed italic">
                  &quot;These ratings reflect feedback collected directly from customers. We don&apos;t claim to be perfect — we claim to show up, do the work right, and stand behind it. If something isn&apos;t right, we make it right.&quot;
                </p>
                <p className="text-sm font-semibold text-gray-900 mt-3">— Kole Southern, Owner, Fix Right Services</p>
              </div>
            </div>
          </div>

          {/* Review Cards */}
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Customer Reviews</h2>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {REVIEWS.map((review, idx) => (
              <ReviewCard key={idx} review={review} />
            ))}
          </div>

          {/* CTA to leave review */}
          <div className="text-center">
            <a
              href="#leave-review"
              className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-8 py-3.5 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="star" className="w-4 h-4" /></span>
              Leave Your Review
            </a>
          </div>
        </div>
      </section>

      {/* Leave a Review Form */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" id="leave-review" aria-label="Leave a review">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12 items-start">

            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
                <div className="mb-7">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Leave Us a Review</h2>
                  <p className="text-gray-600 text-sm">
                    Worked with us? We&apos;d love to hear about your experience. Your feedback helps other Treasure Valley homeowners find trusted help.
                  </p>
                </div>
                <ReviewForm />
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">

              {/* Call CTA */}
              <div className="bg-[#B45309] rounded-2xl p-7 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
                    <Icon name="phone" className="w-6 h-6 text-white" />
                  </span>
                  <div>
                    <p className="text-white/80 text-xs font-medium uppercase tracking-wide">Ready to get started?</p>
                    <p className="font-bold text-lg">Call or Text Us</p>
                  </div>
                </div>
                <a
                  href="tel:+12082046331"
                  className="block text-3xl font-bold text-white hover:text-white/90 transition-colors cursor-pointer mb-1"
                >
                  (208) 204-6331
                </a>
                <p className="text-white/80 text-sm mb-5">Mon–Fri 8am–6pm &nbsp;|&nbsp; Sat 9am–3pm</p>
                <Link
                  to="/contact"
                  className="w-full inline-flex items-center justify-center gap-2 bg-white text-[#B45309] hover:bg-gray-50 px-5 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><Icon name="file-lines" className="w-3.5 h-3.5" /></span>
                  Request a Free Quote
                </Link>
              </div>

              {/* Why reviews matter */}
              <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-4 text-sm">Why Your Review Matters</h3>
                <ul className="space-y-3">
                  {[
                    { icon: 'users', text: 'Helps other Boise homeowners find reliable contractors' },
                    { icon: 'chart-line', text: 'Helps us improve our service and communication' },
                    { icon: 'handshake', text: 'Supports a small, locally owned Treasure Valley business' },
                  ].map((item) => (
                    <li key={item.text} className="flex items-start gap-3 text-sm text-gray-600">
                      <span className="w-8 h-8 flex items-center justify-center bg-[#B45309]/10 rounded-lg flex-shrink-0 mt-0.5">
                        <Icon name={item.icon} className="w-4 h-4 text-[#B45309]" />
                      </span>
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Service areas */}
              <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-4 text-sm">Areas We Serve</h3>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_AREAS.map((area) => (
                    <span
                      key={area}
                      className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs font-medium text-gray-700"
                    >
                      <Icon name="location-dot" className="w-3 h-3 text-[#B45309]" />
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img
                src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp"
                alt="Fix Right Services"
                width="180"
                height="48"
                className="h-12 w-auto mb-4"
                loading="lazy"
              />
              <p className="text-gray-400 text-sm">
                Boise&apos;s trusted experts for fence installation, handyman services, and home remodels. Licensed &amp; insured in Idaho.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Services</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/services/fence" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Fence Installation &amp; Repair</Link></li>
                <li><Link to="/services/handyman-faq" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Handyman Services</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Bathroom Remodels</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Small Remodel Projects</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">About Us</Link></li>
                <li><Link to="/reviews" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Reviews</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Blog &amp; Tips</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Contact</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <Icon name="phone" className="w-3.5 h-3.5 text-[#F59E0B]" />
                  </span>
                  <a href="tel:+12082046331" className="hover:text-white transition-colors cursor-pointer">(208) 204-6331</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <Icon name="envelope" className="w-3.5 h-3.5 text-[#F59E0B]" />
                  </span>
                  <a href="mailto:Southernkole@gmail.com" className="hover:text-white transition-colors cursor-pointer">Southernkole@gmail.com</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <Icon name="location-dot" className="w-3.5 h-3.5 text-[#F59E0B]" />
                  </span>
                  <span>Boise, Meridian, Nampa, Caldwell &amp; Treasure Valley</span>
                </li>
              </ul>
              <div className="flex gap-3 mt-4">
                <a
                  href="https://www.facebook.com/profile.php?id=61582658047880"
                  aria-label="Facebook"
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                >
                  <Icon name="facebook" className="w-5 h-5" />
                </a>
                <a
                  href="https://www.instagram.com/fixright_services/"
                  aria-label="Instagram"
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                >
                  <Icon name="instagram" className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
